/**
 * List page state that survives navigation.
 *
 * A list page is unmounted as soon as the user opens a detail/edit page, so
 * plain `useState` loses the current page, page size and filters: coming back
 * (browser back, a "Retour" button, the sidebar) always lands on page 1 with
 * no filter. `useListState` is a drop-in replacement for `useState` that keeps
 * the value in sessionStorage, scoped to the current route path.
 *
 *   const [page, setPage] = useListState('page', 0);
 *   const [rowsPerPage, setRowsPerPage] = useListState('rowsPerPage', 10);
 *   const [appliedFilters, setAppliedFilters] = useListState<Filters>('appliedFilters', {});
 *
 * Rules for callers:
 *   - Persist only JSON-safe values (strings, numbers, booleans, null, plain
 *     objects/arrays). A `Date`, `Set` or `Map` does not round-trip.
 *   - Persist pagination and filters only — never dialog, menu or selection state.
 *   - Reset the page inside the handlers that change a filter, not in a
 *     `useEffect` on the filters: such an effect also runs on mount and would
 *     wipe the restored page.
 *
 * The state is per tab (sessionStorage), per route path — so
 * `/transporters/12/users` and `/transporters/13/users` keep separate state —
 * and is wiped at login/logout by {@link clearListState}, so a new session
 * never inherits another user's filters.
 */

import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_PREFIX = 'bnft-list-state:';

function buildKey(scope: string, name: string): string {
  // `/a/b/` and `/a/b` are the same page.
  const normalizedScope = scope.length > 1 ? scope.replace(/\/+$/, '') : scope;
  return `${STORAGE_PREFIX}${normalizedScope}:${name}`;
}

function resolveInitial<T>(initialValue: T | (() => T)): T {
  return typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue;
}

function readStoredValue<T>(key: string, initialValue: T | (() => T)): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw !== null && raw !== undefined) {
      return JSON.parse(raw) as T;
    }
  } catch {
    // Storage unavailable or corrupted entry: fall back to the default.
  }
  return resolveInitial(initialValue);
}

function writeStoredValue<T>(key: string, value: T): void {
  try {
    if (value === undefined) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Non-fatal: the list simply won't be restored after navigation.
  }
}

/**
 * `useState` whose value is restored when the user comes back to the same
 * list page during the session.
 *
 * @param name - Name of the value, unique within the page (e.g. `'page'`).
 * @param initialValue - Default used when nothing is stored yet.
 * @param scope - Storage scope; defaults to the current route path. Pass an
 *   explicit scope only to share state between several routes.
 */
export function useListState<T>(
  name: string,
  initialValue: T | (() => T),
  scope?: string,
): [T, Dispatch<SetStateAction<T>>] {
  const { pathname } = useLocation();
  const key = buildKey(scope ?? pathname, name);

  // `touched`: only values the user actually changed are stored. A default is
  // recomputed on every visit instead, so a "today" date filter never turns
  // into yesterday's after midnight just because the page was visited once.
  const [state, setState] = useState(() => ({
    key,
    value: readStoredValue(key, initialValue),
    touched: false,
  }));

  // Same component instance, different route (e.g. `/transporters/12/users`
  // → `/transporters/13/users`): load the state of the new route instead of
  // carrying over the previous one.
  let current = state;
  if (state.key !== key) {
    current = { key, value: readStoredValue(key, initialValue), touched: false };
    setState(current);
  }

  useEffect(() => {
    if (current.touched) {
      writeStoredValue(current.key, current.value);
    }
  }, [current.key, current.value, current.touched]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (action) =>
      setState((prev) => ({
        key: prev.key,
        value:
          typeof action === 'function'
            ? (action as (previous: T) => T)(prev.value)
            : action,
        touched: true,
      })),
    [],
  );

  return [current.value, setValue];
}

/**
 * Bring a restored page index back into range once the total is known.
 *
 * A page restored from the session can point past the end when the data shrank
 * in the meantime (deletions, another user's changes). Without this, the table
 * shows "no results" and MUI warns that the page is out of range.
 *
 * @param page - Current 0-based page index.
 * @param setPage - Page setter.
 * @param total - Total number of rows, `undefined` while loading.
 * @param rowsPerPage - Page size.
 */
export function useClampPage(
  page: number,
  setPage: (page: number) => void,
  total: number | undefined,
  rowsPerPage: number,
): void {
  useEffect(() => {
    if (total === undefined || total === null || page <= 0 || rowsPerPage <= 0) return;
    const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [page, setPage, total, rowsPerPage]);
}

/**
 * Forget every list state of the tab. Called at login and logout so a new
 * session starts from clean lists.
 */
export function clearListState(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const storageKey = sessionStorage.key(i);
      if (storageKey?.startsWith(STORAGE_PREFIX)) {
        keys.push(storageKey);
      }
    }
    keys.forEach((storageKey) => sessionStorage.removeItem(storageKey));
  } catch {
    // Non-fatal.
  }
}
